/**
 * VocabularyTab — Vocabulary management tab for SpecializationDetailPage.
 *
 * Layout (two-column on desktop, stacked on mobile):
 *   Left (2/3): Active vocabulary card + term browser
 *   Right (1/3): Upload zone + version history timeline
 *
 * Reuses VocabularyUploadZone, VocabularyPreviewModal, VersionStatusBadge
 * patterns from SpecializationManagement.tsx.
 */

import { useState, useRef, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, getAuthToken } from '@/lib/api';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface ActiveVocabulary {
  id: string;
  versionNumber: number;
  termCount: number;
  activatedAt: string;
}

interface VocabularyVersion {
  id: string;
  versionNumber: number;
  status: 'DRAFT' | 'ACTIVE' | 'SUPERSEDED' | 'ERROR' | 'PROCESSING';
  createdAt: string;
}

interface VocabularyEntry {
  canonical: string;
  variants: Record<string, string[]>;
  category: string;
}

interface VocabularyContent {
  vocabulary: VocabularyEntry[];
}

interface VocabularyPreviewData {
  data: VocabularyContent;
}

// ---------------------------------------------------------------------------
// VersionStatusBadge
// ---------------------------------------------------------------------------

type VersionStatus = 'DRAFT' | 'ACTIVE' | 'SUPERSEDED' | 'ERROR' | 'PROCESSING';

function VersionStatusBadge({ status }: { status: VersionStatus }) {
  const map: Record<VersionStatus, string> = {
    DRAFT: 'bg-amber-50 text-amber-700 border-amber-200',
    ACTIVE: 'bg-teal-50 text-teal-700 border-teal-200',
    SUPERSEDED: 'bg-gray-100 text-gray-500 border-gray-200',
    ERROR: 'bg-red-50 text-red-700 border-red-200',
    PROCESSING: 'bg-blue-50 text-blue-700 border-blue-200',
  };
  const labels: Record<VersionStatus, string> = {
    DRAFT: 'Draft',
    ACTIVE: 'Active',
    SUPERSEDED: 'Superseded',
    ERROR: 'Error',
    PROCESSING: 'Processing…',
  };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold border ${map[status]}`}>
      {status === 'PROCESSING' && (
        <span className="w-2 h-2 rounded-full border border-blue-600 border-t-transparent animate-spin me-1.5" />
      )}
      {labels[status]}
    </span>
  );
}

// ---------------------------------------------------------------------------
// VocabularyPreviewModal
// ---------------------------------------------------------------------------

interface VocabularyPreviewModalProps {
  open: boolean;
  specializationId: string;
  versionId: string;
  versionNumber: number;
  onClose: () => void;
}

function VocabularyPreviewModal({ open, specializationId, versionId, versionNumber, onClose }: VocabularyPreviewModalProps) {
  const { data, isLoading } = useQuery<VocabularyPreviewData>({
    queryKey: ['admin', 'specializations', specializationId, 'vocabulary', versionId, 'preview'],
    queryFn: () => api.get<VocabularyPreviewData>(`/admin/specializations/${specializationId}/vocabulary/${versionId}/preview`),
    enabled: open,
  });

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-clinical-charcoal/50 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-clinical-lg max-w-2xl w-full max-h-[80vh] flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-clinical-border flex-none">
          <h3 className="font-heading text-base font-semibold text-clinical-charcoal">
            Vocabulary Preview — v{versionNumber}
          </h3>
          <button type="button" onClick={onClose} className="text-clinical-text-muted hover:text-clinical-charcoal transition-colors">
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        <div className="flex-1 overflow-auto p-6">
          {isLoading ? (
            <div className="space-y-3">
              {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="skeleton h-4 w-full rounded" />
              ))}
            </div>
          ) : (
            <pre className="text-xs font-mono text-clinical-charcoal whitespace-pre-wrap bg-clinical-cream rounded-clinical p-4 border border-clinical-border">
              {JSON.stringify(data?.data ?? data, null, 2)}
            </pre>
          )}
        </div>
        <div className="px-6 py-4 border-t border-clinical-border flex-none">
          <p className="text-xs font-body text-clinical-text-muted">
            Read-only view of the clinical vocabulary JSON for this version.
          </p>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// VocabularyUploadZone
// ---------------------------------------------------------------------------

interface VocabularyUploadZoneProps {
  specializationId: string;
  onUploaded: () => void;
}

function VocabularyUploadZone({ specializationId, onUploaded }: VocabularyUploadZoneProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [fileError, setFileError] = useState('');
  const [isUploading, setIsUploading] = useState(false);

  const upload = useCallback(async (file: File) => {
    const ext = file.name.split('.').pop()?.toLowerCase() ?? '';
    if (ext !== 'json') {
      setFileError('Only .json files are supported.');
      return;
    }
    if (file.size > 1 * 1024 * 1024) {
      setFileError('File must be under 1MB.');
      return;
    }

    setFileError('');
    setIsUploading(true);

    try {
      const formData = new FormData();
      formData.append('file', file);

      const BASE = (import.meta.env.VITE_API_URL as string | undefined) ?? '';
      const token = await getAuthToken();
      const res = await fetch(`${BASE}/admin/specializations/${specializationId}/vocabulary/upload`, {
        method: 'POST',
        body: formData,
        credentials: 'include',
        ...(token ? { headers: { Authorization: `Bearer ${token}` } } : {}),
      });
      if (!res.ok) {
        const body = await res.json() as { message?: string };
        throw new Error(body.message ?? `Upload failed (${res.status})`);
      }
      onUploaded();
    } catch (err: unknown) {
      setFileError(err instanceof Error ? err.message : 'Upload failed.');
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  }, [specializationId, onUploaded]);

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (file) void upload(file);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) void upload(file);
  };

  return (
    <div>
      <div
        onDrop={handleDrop}
        onDragOver={(e) => e.preventDefault()}
        onClick={() => fileInputRef.current?.click()}
        className={`border-2 border-dashed rounded-clinical p-4 text-center cursor-pointer transition-colors ${
          isUploading
            ? 'border-clinical-teal bg-teal-50 cursor-wait'
            : 'border-clinical-border hover:border-clinical-teal hover:bg-clinical-cream/50'
        }`}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept=".json"
          onChange={handleFileChange}
          className="hidden"
          disabled={isUploading}
        />
        {isUploading ? (
          <div className="flex flex-col items-center gap-2">
            <div className="w-5 h-5 border-2 border-clinical-teal border-t-transparent rounded-full animate-spin" />
            <p className="text-xs font-body text-clinical-teal">Uploading…</p>
          </div>
        ) : (
          <div className="flex flex-col items-center gap-1">
            <svg className="w-5 h-5 text-clinical-text-muted" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5m-13.5-9L12 3m0 0l4.5 4.5M12 3v13.5" />
            </svg>
            <p className="text-xs font-body text-clinical-text-muted">
              Drop a vocabulary file or <span className="text-clinical-teal font-medium">browse</span>
            </p>
            <p className="text-xs font-body text-clinical-text-muted">.json only — max 1MB</p>
          </div>
        )}
      </div>
      {fileError && (
        <p className="text-xs font-body text-red-600 mt-1">{fileError}</p>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// ActiveVocabularyCard
// ---------------------------------------------------------------------------

function ActiveVocabularyCard({
  activeVocabulary,
  specializationId,
}: {
  activeVocabulary: ActiveVocabulary | null;
  specializationId: string;
}) {
  const formatDate = (iso: string) =>
    new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

  const handleDownload = async () => {
    if (!activeVocabulary) return;
    try {
      const BASE = (import.meta.env.VITE_API_URL as string | undefined) ?? '';
      const token = await getAuthToken();
      const res = await fetch(
        `${BASE}/admin/specializations/${specializationId}/vocabulary/${activeVocabulary.id}/download`,
        {
          credentials: 'include',
          ...(token ? { headers: { Authorization: `Bearer ${token}` } } : {}),
        },
      );
      if (!res.ok) throw new Error(`Download failed (${res.status})`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `vocabulary-v${activeVocabulary.versionNumber}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch {
      // Silently fail — user will see no download, not ideal but non-critical
    }
  };

  if (!activeVocabulary) {
    return (
      <div className="border-l-4 border-clinical-border bg-clinical-cream/50 rounded-clinical p-4">
        <h4 className="font-heading text-sm font-semibold text-clinical-charcoal mb-1">Active Vocabulary</h4>
        <p className="text-xs font-body text-clinical-text-muted">
          No vocabulary activated. Upload a vocabulary JSON file and activate it to enable medical term replacement in translations.
        </p>
      </div>
    );
  }

  return (
    <div className="border-l-4 border-clinical-teal bg-teal-50/30 rounded-clinical p-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <h4 className="font-heading text-sm font-semibold text-clinical-charcoal">Active Vocabulary</h4>
            <VersionStatusBadge status="ACTIVE" />
          </div>
          <p className="text-xs font-body text-clinical-text-muted">
            Version {activeVocabulary.versionNumber} &bull; {activeVocabulary.termCount} terms &bull; Activated {formatDate(activeVocabulary.activatedAt)}
          </p>
        </div>
        <button
          type="button"
          onClick={() => void handleDownload()}
          className="flex items-center gap-1.5 text-xs text-clinical-teal font-medium hover:underline flex-none"
          title="Download vocabulary JSON"
        >
          <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5M16.5 12L12 16.5m0 0L7.5 12m4.5 4.5V3" />
          </svg>
          Download
        </button>
      </div>

      <div className="mt-3 grid grid-cols-2 gap-2">
        <div className="flex items-center gap-1.5 text-xs font-body text-clinical-text-muted">
          <svg className="w-3.5 h-3.5 text-clinical-teal flex-none" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
          </svg>
          Arabic → English glossary
        </div>
        <div className="flex items-center gap-1.5 text-xs font-body text-clinical-text-muted">
          <svg className="w-3.5 h-3.5 text-clinical-teal flex-none" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
          </svg>
          English → Arabic glossary
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// TermBrowser
// ---------------------------------------------------------------------------

interface TermBrowserProps {
  specializationId: string;
  vocabularyVersionId: string;
}

function TermBrowser({ specializationId, vocabularyVersionId }: TermBrowserProps) {
  const [search, setSearch] = useState('');
  const [activeCategory, setActiveCategory] = useState<string | null>(null);

  const { data, isLoading } = useQuery<VocabularyPreviewData>({
    queryKey: ['admin', 'specializations', specializationId, 'vocabulary', vocabularyVersionId, 'preview'],
    queryFn: () => api.get<VocabularyPreviewData>(`/admin/specializations/${specializationId}/vocabulary/${vocabularyVersionId}/preview`),
  });

  const vocabulary = data?.data?.vocabulary ?? [];

  // Collect unique categories
  const categories = Array.from(new Set(vocabulary.map((e) => e.category).filter(Boolean)));
  const categoryCounts = Object.fromEntries(
    categories.map((cat) => [cat, vocabulary.filter((e) => e.category === cat).length])
  );

  // Filter entries
  const filtered = vocabulary.filter((entry) => {
    const matchesCategory = !activeCategory || entry.category === activeCategory;
    if (!matchesCategory) return false;
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    if (entry.canonical.toLowerCase().includes(q)) return true;
    for (const variants of Object.values(entry.variants)) {
      if (variants.some((v) => v.toLowerCase().includes(q))) return true;
    }
    return false;
  });

  if (isLoading) {
    return (
      <div className="space-y-2 mt-4">
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className="skeleton h-14 w-full rounded" />
        ))}
      </div>
    );
  }

  if (vocabulary.length === 0) return null;

  return (
    <div className="mt-4">
      {/* Search */}
      <div className="relative mb-3">
        <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-clinical-text-muted" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z" />
        </svg>
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search terms or variants…"
          className="form-input ps-9 py-2 text-sm"
        />
      </div>

      {/* Category pills */}
      {categories.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mb-3">
          <button
            type="button"
            onClick={() => setActiveCategory(null)}
            className={`px-2.5 py-1 rounded-full text-xs font-medium border transition-colors ${
              !activeCategory
                ? 'bg-clinical-teal text-white border-clinical-teal'
                : 'bg-white text-clinical-text-muted border-clinical-border hover:border-clinical-teal'
            }`}
          >
            All <span className="opacity-75">({vocabulary.length})</span>
          </button>
          {categories.map((cat) => (
            <button
              key={cat}
              type="button"
              onClick={() => setActiveCategory(cat === activeCategory ? null : cat)}
              className={`px-2.5 py-1 rounded-full text-xs font-medium border transition-colors ${
                activeCategory === cat
                  ? 'bg-clinical-teal text-white border-clinical-teal'
                  : 'bg-white text-clinical-text-muted border-clinical-border hover:border-clinical-teal'
              }`}
            >
              {cat} <span className="opacity-75">({categoryCounts[cat]})</span>
            </button>
          ))}
        </div>
      )}

      {/* Term list */}
      <div className="space-y-2 max-h-96 overflow-y-auto">
        {filtered.length === 0 ? (
          <p className="text-xs font-body text-clinical-text-muted py-4 text-center">No terms match your search.</p>
        ) : (
          filtered.map((entry, idx) => (
            <div key={idx} className="bg-clinical-cream/50 border border-clinical-border rounded-clinical p-3">
              <div className="flex items-start justify-between gap-2 mb-1.5">
                <p className="text-sm font-medium font-body text-clinical-charcoal">{entry.canonical}</p>
                {entry.category && (
                  <span className="px-1.5 py-0.5 bg-white border border-clinical-border rounded text-xs font-body text-clinical-text-muted flex-none">
                    {entry.category}
                  </span>
                )}
              </div>
              <div className="space-y-1">
                {Object.entries(entry.variants).map(([lang, variants]) => (
                  <p key={lang} className="text-xs font-body text-clinical-text-muted">
                    <span className="uppercase font-semibold text-clinical-charcoal/60 me-1.5">{lang}</span>
                    {variants.join(', ')}
                  </p>
                ))}
              </div>
            </div>
          ))
        )}
      </div>

      <p className="text-xs font-body text-clinical-text-muted mt-2">
        Showing {filtered.length} of {vocabulary.length} terms
      </p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// VersionTimeline
// ---------------------------------------------------------------------------

interface VersionTimelineProps {
  specializationId: string;
  onActivated: () => void;
}

function VersionTimeline({ specializationId, onActivated }: VersionTimelineProps) {
  const queryClient = useQueryClient();
  const [previewVersion, setPreviewVersion] = useState<VocabularyVersion | null>(null);

  const { data: versions, isLoading } = useQuery<VocabularyVersion[]>({
    queryKey: ['admin', 'specializations', specializationId, 'vocabulary'],
    queryFn: async () => {
      const res = await api.get<{ data: VocabularyVersion[] }>(`/admin/specializations/${specializationId}/vocabulary`);
      return res.data;
    },
  });

  const activateMutation = useMutation({
    mutationFn: ({ versionId }: { versionId: string }) =>
      api.post(`/admin/specializations/${specializationId}/vocabulary/${versionId}/activate`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['admin', 'specializations', specializationId] });
      void queryClient.invalidateQueries({ queryKey: ['admin', 'specializations', specializationId, 'vocabulary'] });
      onActivated();
    },
  });

  const formatDate = (iso: string) =>
    new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

  if (isLoading) {
    return (
      <div className="space-y-2">
        {Array.from({ length: 2 }).map((_, i) => (
          <div key={i} className="skeleton h-12 w-full rounded" />
        ))}
      </div>
    );
  }

  if (!versions || versions.length === 0) {
    return (
      <p className="text-xs font-body text-clinical-text-muted">No versions yet.</p>
    );
  }

  return (
    <div className="space-y-0">
      {versions.map((v, idx) => (
        <div key={v.id} className="flex gap-3">
          {/* Timeline connector */}
          <div className="flex flex-col items-center flex-none w-5">
            <div className={`w-3 h-3 rounded-full mt-1 flex-none border-2 ${
              v.status === 'ACTIVE'
                ? 'bg-clinical-teal border-clinical-teal'
                : 'bg-white border-gray-300'
            }`} />
            {idx < versions.length - 1 && (
              <div className="w-px flex-1 bg-gray-200 mt-1" />
            )}
          </div>

          {/* Content */}
          <div className="pb-3 flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-0.5">
              <span className="font-mono text-xs font-semibold text-clinical-charcoal">v{v.versionNumber}</span>
              <VersionStatusBadge status={v.status} />
            </div>
            <p className="text-xs font-body text-clinical-text-muted">{formatDate(v.createdAt)}</p>
            <div className="flex items-center gap-2 mt-1">
              <button
                type="button"
                onClick={() => setPreviewVersion(v)}
                className="text-xs text-clinical-text-muted font-medium hover:text-clinical-charcoal"
              >
                Preview
              </button>
              {v.status === 'DRAFT' && (
                <>
                  <span className="text-clinical-border">|</span>
                  <button
                    type="button"
                    onClick={() => activateMutation.mutate({ versionId: v.id })}
                    disabled={activateMutation.isPending}
                    className="text-xs text-clinical-teal font-medium hover:underline disabled:opacity-50"
                  >
                    Activate
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      ))}

      {previewVersion && (
        <VocabularyPreviewModal
          open
          specializationId={specializationId}
          versionId={previewVersion.id}
          versionNumber={previewVersion.versionNumber}
          onClose={() => setPreviewVersion(null)}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// VocabularyTab — public export
// ---------------------------------------------------------------------------

interface VocabularyTabProps {
  specializationId: string;
  activeVocabulary: ActiveVocabulary | null;
  onRefresh: () => void;
}

export function VocabularyTab({ specializationId, activeVocabulary, onRefresh }: VocabularyTabProps) {
  const queryClient = useQueryClient();

  const handleRefresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['admin', 'specializations', specializationId] });
    void queryClient.invalidateQueries({ queryKey: ['admin', 'specializations', specializationId, 'vocabulary'] });
    onRefresh();
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      {/* Left column (2/3 width on desktop) */}
      <div className="lg:col-span-2 space-y-4">
        {/* Active vocabulary card */}
        <ActiveVocabularyCard
          activeVocabulary={activeVocabulary}
          specializationId={specializationId}
        />

        {/* Term browser — shown only when active vocabulary exists */}
        {activeVocabulary && (
          <div className="clinical-card p-4">
            <h4 className="font-heading text-sm font-semibold text-clinical-charcoal mb-1">Term Browser</h4>
            <p className="text-xs font-body text-clinical-text-muted mb-3">
              Search and browse all {activeVocabulary.termCount} terms in the active vocabulary.
            </p>
            <TermBrowser
              specializationId={specializationId}
              vocabularyVersionId={activeVocabulary.id}
            />
          </div>
        )}
      </div>

      {/* Right column (1/3 width on desktop) */}
      <div className="space-y-4">
        {/* Upload zone */}
        <div className="clinical-card p-4">
          <h4 className="font-heading text-sm font-semibold text-clinical-charcoal mb-1">Upload New Version</h4>
          <p className="text-xs font-body text-clinical-text-muted mb-3">
            Upload a JSON vocabulary file to create a new draft version.
          </p>
          <VocabularyUploadZone
            specializationId={specializationId}
            onUploaded={handleRefresh}
          />
        </div>

        {/* Version history */}
        <div className="clinical-card p-4">
          <h4 className="font-heading text-sm font-semibold text-clinical-charcoal mb-3">Version History</h4>
          <VersionTimeline
            specializationId={specializationId}
            onActivated={handleRefresh}
          />
        </div>
      </div>
    </div>
  );
}
